import { useMemo, useState } from 'react'
import {
  AlertTriangle,
  Loader2,
  MapPin,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import {
  useCreateMachineLocation,
  useDeleteMachineLocation,
  useListHazardIdentifications,
  useMachineLocations,
  useUpdateMachineLocation,
  type MachineLocation,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { ComplianceSubPageHeader } from '../../components/ComplianceSubPageHeader'
import { ComplianceSubPageTitle } from '../../components/ComplianceSubPageTitle'
import {
  buildMachineLocationRows,
  canDeleteMachineLocation,
  canEditMachineLocation,
  formatEvidence,
  getMachineLocationDisplayDescription,
  getMachineLocationDisplayName,
  isSystemMachineLocation,
  type MachineLocationRow,
} from './buildMachineLocationRows'
import { DeleteMachineLocationDialog } from './DeleteMachineLocationDialog'
import {
  EMPTY_MACHINE_LOCATION_FORM,
  isMachineLocationFormValid,
  MachineLocationFormDialog,
  type MachineLocationFormState,
} from './MachineLocationFormDialog'
import { useMachineLocationGeneration } from './useMachineLocationGeneration'

export function MachineLocationsPage() {
  const params = useParams<{
    productId: string
    machineryDirectiveComplianceId: string
  }>()
  const complianceId = params.machineryDirectiveComplianceId!
  const productId = params.productId
  const { t } = useTranslation('machineryDirectiveCompliance')

  const {
    data: locations = [],
    isLoading: isLoadingLocations,
    isError: isLocationsError,
    error: locationsError,
    refetch: refetchLocations,
  } = useMachineLocations(complianceId)
  const {
    data: hazards = [],
    isLoading: isLoadingHazards,
    isError: isHazardsError,
    error: hazardsError,
    refetch: refetchHazards,
  } = useListHazardIdentifications(complianceId)

  const createMutation = useCreateMachineLocation(complianceId)
  const updateMutation = useUpdateMachineLocation(complianceId)
  const deleteMutation = useDeleteMachineLocation(complianceId)
  const {
    handleGenerate,
    isRunning,
    statusMessage,
    noNewLocationsMessage,
    generateDisabledDueToSources,
  } = useMachineLocationGeneration({
    complianceId,
    productId,
    copyKeyPrefix: 'machineLocationsPage',
  })

  const [dialogMode, setDialogMode] = useState<'add' | 'edit' | null>(null)
  const [editingLocation, setEditingLocation] =
    useState<MachineLocation | null>(null)
  const [locationToDelete, setLocationToDelete] =
    useState<MachineLocationRow | null>(null)
  const [form, setForm] = useState<MachineLocationFormState>(
    EMPTY_MACHINE_LOCATION_FORM
  )

  const isMutationPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending
  const isLocked = isRunning || isMutationPending
  const isLoading = isLoadingLocations || isLoadingHazards
  const isError = isLocationsError || isHazardsError
  const errorMessage = locationsError?.message ?? hazardsError?.message

  const rows = useMemo(
    () => buildMachineLocationRows(locations, hazards),
    [locations, hazards]
  )

  function openAddDialog() {
    setEditingLocation(null)
    setForm(EMPTY_MACHINE_LOCATION_FORM)
    setDialogMode('add')
  }

  function openEditDialog(location: MachineLocation) {
    setEditingLocation(location)
    setForm({
      name: location.name,
      description: location.description,
    })
    setDialogMode('edit')
  }

  function closeDialog() {
    setDialogMode(null)
    setEditingLocation(null)
    setForm(EMPTY_MACHINE_LOCATION_FORM)
  }

  function handleSaveLocation() {
    if (!isMachineLocationFormValid(form)) {
      return
    }

    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
    }

    if (dialogMode === 'add') {
      createMutation.mutate(payload, {
        onSuccess: () => {
          closeDialog()
          toast.success(t('machineLocationsPage.createSuccess'))
        },
        onError: (error) => {
          toast.error(
            error.message || t('machineLocationsPage.saveFailureCreate')
          )
        },
      })
      return
    }

    if (dialogMode === 'edit' && editingLocation != null) {
      updateMutation.mutate(
        {
          machineLocationId: editingLocation.id,
          ...payload,
        },
        {
          onSuccess: () => {
            closeDialog()
            toast.success(t('machineLocationsPage.updateSuccess'))
          },
          onError: (error) => {
            toast.error(
              error.message || t('machineLocationsPage.saveFailureUpdate')
            )
          },
        }
      )
    }
  }

  function handleConfirmDelete() {
    if (locationToDelete == null) {
      return
    }

    deleteMutation.mutate(locationToDelete.id, {
      onSuccess: () => {
        setLocationToDelete(null)
        toast.success(t('machineLocationsPage.deleteSuccess'))
      },
      onError: (error) => {
        toast.error(error.message || t('machineLocationsPage.deleteFailure'))
      },
    })
  }

  const handleRetry = () => {
    void refetchLocations()
    void refetchHazards()
  }

  const dialogTitle =
    dialogMode === 'edit'
      ? t('machineLocationsPage.editTitle')
      : t('machineLocationsPage.addTitle')

  const generateButton = (
    <Button
      type='button'
      variant='outline'
      disabled={isLocked || generateDisabledDueToSources}
      onClick={handleGenerate}
    >
      {isRunning ? (
        <Loader2 className='mr-2 size-4 animate-spin' />
      ) : (
        <Sparkles className='mr-2 size-4' />
      )}
      {t('machineLocationsPage.generateButton')}
    </Button>
  )

  return (
    <div className='flex min-h-0 flex-1 flex-col'>
      <ComplianceSubPageHeader>
        <div className='space-y-1'>
          <ComplianceSubPageTitle>
            {t('machineLocationsPage.pageTitle')}
          </ComplianceSubPageTitle>
          <p className='text-sm text-muted-foreground'>
            {t('machineLocationsPage.pageDescription')}
          </p>
        </div>
        <div className='flex flex-wrap gap-2'>
          <Button type='button' onClick={openAddDialog} disabled={isLocked}>
            <Plus className='mr-2 size-4' />
            {t('machineLocationsPage.addButton')}
          </Button>
          {generateDisabledDueToSources ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className='inline-flex'>{generateButton}</span>
              </TooltipTrigger>
              <TooltipContent side='top' className='max-w-sm'>
                {t('machineLocationsPage.generateDisabledTooltip')}
              </TooltipContent>
            </Tooltip>
          ) : (
            generateButton
          )}
        </div>
      </ComplianceSubPageHeader>

      {isRunning && (
        <Alert className='mb-4 border-primary/50 bg-primary/5'>
          <AlertTriangle className='size-4' />
          <AlertTitle>{t('machineLocationsPage.generating')}</AlertTitle>
          <AlertDescription>
            {statusMessage ?? t('machineLocationsPage.generateProgressInitial')}
          </AlertDescription>
        </Alert>
      )}

      {noNewLocationsMessage && !isRunning ? (
        <p className='mb-4 text-sm text-muted-foreground' role='status'>
          {t('machineLocationsPage.noNewLocationsFound')}
        </p>
      ) : null}

      {isError && (
        <Alert variant='destructive' className='mb-4'>
          <AlertTriangle className='size-4' />
          <AlertTitle>{t('machineLocationsPage.loadFailedTitle')}</AlertTitle>
          <AlertDescription className='flex flex-wrap items-center gap-3'>
            <span>
              {errorMessage ?? t('machineLocationsPage.loadFailed')}
            </span>
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={handleRetry}
            >
              {t('machineLocationsPage.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {isLoading && (
        <div className='space-y-2'>
          <Skeleton className='h-10 w-full rounded-md' />
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className='h-14 w-full rounded-md' />
          ))}
        </div>
      )}

      {!isLoading && !isError && rows.length === 0 && (
        <Empty className='border border-dashed'>
          <EmptyHeader>
            <EmptyMedia variant='icon'>
              <MapPin />
            </EmptyMedia>
            <EmptyTitle>{t('machineLocationsPage.emptyTitle')}</EmptyTitle>
            <EmptyDescription>
              {t('machineLocationsPage.emptyDescription')}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button
              type='button'
              size='sm'
              onClick={openAddDialog}
              disabled={isLocked}
            >
              <Plus className='mr-2 size-4' />
              {t('machineLocationsPage.addButton')}
            </Button>
          </EmptyContent>
        </Empty>
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <div className='rounded-md border'>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('machineLocationsPage.nameLabel')}</TableHead>
                <TableHead>
                  {t('machineLocationsPage.descriptionLabel')}
                </TableHead>
                <TableHead>{t('machineLocationsPage.hazardsLabel')}</TableHead>
                <TableHead>{t('machineLocationsPage.evidenceLabel')}</TableHead>
                <TableHead className='w-[52px]'>
                  <span className='sr-only'>
                    {t('machineLocationsPage.actionsLabel')}
                  </span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((location) => {
                const linkedHazardCount = location.linkedHazards.length
                const isEditable = canEditMachineLocation(location)
                const isDeletable = canDeleteMachineLocation(location)
                const isSystemLocation = isSystemMachineLocation(location)

                const displayName = getMachineLocationDisplayName(location, t)
                const displayDescription = getMachineLocationDisplayDescription(
                  location,
                  t
                )

                const editButton = (
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    disabled={isLocked || !isEditable}
                    aria-label={t('machineLocationsPage.editAriaLabel', {
                      name: displayName,
                    })}
                    onClick={() => {
                      if (isEditable) {
                        openEditDialog(location)
                      }
                    }}
                  >
                    <Pencil className='size-4' />
                  </Button>
                )

                const deleteButton = (
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    disabled={isLocked || !isDeletable}
                    aria-label={t('machineLocationsPage.deleteAriaLabel', {
                      name: displayName,
                    })}
                    onClick={() => {
                      if (isDeletable) {
                        setLocationToDelete(location)
                      }
                    }}
                  >
                    <Trash2 className='size-4' />
                  </Button>
                )

                return (
                  <TableRow key={location.id}>
                    <TableCell className='align-top font-medium'>
                      {displayName}
                    </TableCell>
                    <TableCell className='align-top whitespace-pre-wrap text-muted-foreground'>
                      {displayDescription}
                    </TableCell>
                    <TableCell className='align-top text-muted-foreground'>
                      {linkedHazardCount}
                    </TableCell>
                    <TableCell className='align-top whitespace-pre-wrap text-muted-foreground'>
                      {formatEvidence(location.reasoning)}
                    </TableCell>
                    <TableCell className='align-top'>
                      <div className='flex items-center gap-1'>
                        {isEditable ? (
                          editButton
                        ) : (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className='inline-flex'>{editButton}</span>
                            </TooltipTrigger>
                            <TooltipContent side='top' className='max-w-sm'>
                              {t(
                                'machineLocationsPage.systemLocationDisabledTooltip'
                              )}
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {isDeletable ? (
                          deleteButton
                        ) : (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className='inline-flex'>{deleteButton}</span>
                            </TooltipTrigger>
                            <TooltipContent side='top' className='max-w-sm'>
                              {isSystemLocation
                                ? t(
                                    'machineLocationsPage.systemLocationDisabledTooltip'
                                  )
                                : t('machineLocationsPage.deleteDisabledTooltip')}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <MachineLocationFormDialog
        open={dialogMode != null}
        title={dialogTitle}
        form={form}
        nameLabel={t('machineLocationsPage.nameLabel')}
        descriptionLabel={t('machineLocationsPage.descriptionLabel')}
        saveLabel={t('machineLocationsPage.save')}
        cancelLabel={t('machineLocationsPage.cancel')}
        isPending={isLocked}
        onOpenChange={(open) => {
          if (!open) {
            closeDialog()
          }
        }}
        onFormChange={setForm}
        onSubmit={handleSaveLocation}
      />

      <DeleteMachineLocationDialog
        isOpen={locationToDelete != null}
        locationName={
          locationToDelete != null
            ? getMachineLocationDisplayName(locationToDelete, t)
            : ''
        }
        onClose={() => setLocationToDelete(null)}
        onConfirm={handleConfirmDelete}
        isDeleting={deleteMutation.isPending || isRunning}
      />
    </div>
  )
}
